import React, { useEffect, useState } from 'react';
import { getPatientPayableClaims } from '../services/paymentsService.js';

// The claim picker shared by the insurance invoice (one per patient row) and
// Collect payment's "This is an insurance payment". Linking a claim is what
// lets the payment post to the PMS as an insurance payment on that claim.

// A claim's date is the service day at the practice, stored as that local
// midnight in UTC (e.g. 2026-10-19T04:00:00Z for Eastern). Formatting in the
// viewer's timezone shows the day before anywhere west of the practice, so
// read it in UTC, which keeps the practice's date for any US timezone.
const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

export const claimLabel = (c) => [
  fmtDate(c.claimTime),
  c.carrier || 'Unknown carrier',
  c.insuranceType ? c.insuranceType.toLowerCase() : null,
  `billed $${(c.billedAmount || 0).toFixed(2)}`,
  c.paidAmount ? `paid $${c.paidAmount.toFixed(2)}` : null,
  c.state ? c.state.toLowerCase() : null,
].filter(Boolean).join(' · ');

// A patient's claims an insurance payment can be linked to.
export const usePatientClaims = (practiceId, patientId) => {
  const [state, setState] = useState({ loading: false, claims: [] });
  useEffect(() => {
    if (!practiceId || !patientId) {
      setState({ loading: false, claims: [] });
      return undefined;
    }
    let cancelled = false;
    setState({ loading: true, claims: [] });
    getPatientPayableClaims(practiceId, patientId)
      .then((data) => { if (!cancelled) setState({ loading: false, claims: data?.claims || [] }); })
      .catch(() => { if (!cancelled) setState({ loading: false, claims: [] }); });
    return () => { cancelled = true; };
  }, [practiceId, patientId]);
  return state;
};

const ClaimSelect = ({ claims, loading, value, onChange, takenClaims, ariaLabel, style }) => {
  const noClaims = !loading && claims.length === 0;
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      disabled={loading || noClaims}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...style, color: noClaims ? '#6b716d' : undefined }}
      title={noClaims ? 'This patient has no claims in the PMS — post their payment there yourself.' : undefined}
    >
      <option value="">
        {loading ? 'Loading claims…' : noClaims ? 'No claims on file' : 'No claim (post in the PMS yourself)'}
      </option>
      {claims.map((c) => (
        <option key={c.name} value={c.name} disabled={c.name !== value && !!takenClaims?.has(c.name)}>
          {claimLabel(c)}
        </option>
      ))}
    </select>
  );
};

export default ClaimSelect;
